// CruxToggle — kompakter Toggle-Button neben "Navigate to start" (User-Wunsch
// Batch 7). Zeigt das klassische "Gefahr!"-Dreieck (gelb/schwarz mit Ausruf-
// zeichen) als Icon. Tap = alle vom Admin markierten Schlüsselstellen auf
// Karte + Höhenprofil ein-/ausblenden.
//
// Varianten:
//   - compact  : gleicher Look wie der compact-NavigateToTrailhead; gedacht
//                für die Button-Reihe im Trail-Detail-Sheet im Explore-Mode.
//   - floating : runder 36x36-Pill oben rechts im Ride-Mode-Mini-Strip
//                (User-Wunsch: Toggle soll auch sichtbar bleiben während
//                man fährt, damit man's spontan ausblenden kann wenn's nervt).
//
// Der Hazard-Triangle ist ein reines SVG — kein Emoji, weil die gelb/schwarze
// Farbgebung zuverlässig aussehen soll (🚨, ⚠️ etc. werden auf manchen Systemen
// nur rot oder in monochrom gerendert).

import { useShowCruxPoints } from '../lib/useUiSettings';

export interface CruxToggleProps {
  variant?: 'compact' | 'floating';
  /**
   * Anzahl Schlüsselstellen auf dem aktuell selektierten Trail — wird als
   * kleiner Badge angezeigt. Bei 0 bleibt der Button aktiv, zeigt aber keinen
   * Zähler (der User soll den Toggle erkunden können, auch wenn dieser Trail
   * noch keine Markierungen hat).
   */
  count?: number;
}

/**
 * SVG: klassisches 3-Ecks-Warndreieck, gelb gefüllt, schwarzer Rahmen + '!'.
 * viewBox absichtlich 0..20 damit das Fragezeichen scharf auf kleinen Größen
 * rendert. Der Ausrufezeichen-Balken ist ein 2px-Stamm + 2.5px-Dot.
 */
export function HazardTriangleIcon({ size = 16, dim = false }: { size?: number; dim?: boolean }) {
  // Wenn der Toggle AUS ist, dimmen wir das Icon (Opacity + leicht
  // desaturiertes Gelb) damit klar ist "ausgeschaltet".
  const fill = dim ? '#a16207' : '#facc15'; // amber-700 dim vs. yellow-400 active
  const stroke = dim ? '#1f2937' : '#111827';
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      aria-hidden="true"
      style={{ display: 'block' }}
    >
      <path
        d="M10 1.8 L18.6 17 L1.4 17 Z"
        fill={fill}
        stroke={stroke}
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      {/* Ausrufezeichen: Stamm + Dot in schwarz. */}
      <rect x="9" y="7" width="2" height="5.4" rx="0.4" fill={stroke} />
      <circle cx="10" cy="14.4" r="1.15" fill={stroke} />
    </svg>
  );
}

export function CruxToggle({ variant = 'compact', count = 0 }: CruxToggleProps) {
  const [on, setOn] = useShowCruxPoints();

  const label = on ? 'Hide key sections' : 'Show key sections';
  const title = `${label}${count > 0 ? ` (${count})` : ''}`;

  // Floating-Variante: runder Pill 36x36, ähnlich den Locate/Compass-Controls.
  // Kleiner Opacity-Ring wenn aus, damit "off"-State sofort erkennbar ist.
  if (variant === 'floating') {
    return (
      <button
        type="button"
        onClick={() => setOn(!on)}
        title={title}
        aria-label={title}
        aria-pressed={on}
        className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors pointer-events-auto relative ${
          on
            ? 'bg-turbo-900/85 hover:bg-turbo-900/95 border border-turbo-500/40'
            : 'bg-turbo-900/50 hover:bg-turbo-900/70 border border-white/10'
        }`}
      >
        <HazardTriangleIcon size={18} dim={!on} />
        {count > 0 && (
          <span
            className={`absolute -top-1 -right-1 min-w-[14px] h-[14px] px-0.5 text-[9px] leading-[14px] font-semibold rounded-full text-center ${
              on ? 'bg-amber-400 text-black' : 'bg-white/40 text-black/70'
            }`}
          >
            {count}
          </span>
        )}
      </button>
    );
  }

  // Compact-Variante: fügt sich in die Button-Row im Detail-Sheet ein.
  // Zwei-Zeilen-Label wie bei "Navigate to start", damit Höhe identisch bleibt.
  return (
    <button
      type="button"
      onClick={() => setOn(!on)}
      title={title}
      aria-label={title}
      aria-pressed={on}
      className={`flex-shrink-0 transition-colors px-2.5 py-1.5 rounded-lg flex items-center justify-center gap-1.5 border ${
        on
          ? 'bg-turbo-700/50 hover:bg-turbo-700/70 active:bg-turbo-700/90 text-white border-turbo-500/30'
          : 'bg-turbo-700/20 hover:bg-turbo-700/40 active:bg-turbo-700/60 text-white/75 border-white/10'
      }`}
    >
      <span className="flex-shrink-0 relative">
        <HazardTriangleIcon size={18} dim={!on} />
        {count > 0 && (
          <span
            className={`absolute -top-1.5 -right-2 min-w-[14px] h-[14px] px-0.5 text-[9px] leading-[14px] font-semibold rounded-full text-center ${
              on ? 'bg-amber-400 text-black' : 'bg-white/40 text-black/70'
            }`}
          >
            {count}
          </span>
        )}
      </span>
      <span className="text-[12px] font-medium leading-tight text-center">
        Key
        <br />
        sections
      </span>
    </button>
  );
}

// Reusable hazard-icon SVG string — für maplibregl.Marker HTML und den
// ElevationProfile-Marker. Gleicher Look wie in HazardTriangleIcon oben,
// aber ohne JSX (für innerHTML + SVG im Profil-SVG).
export const HAZARD_TRIANGLE_SVG_MARKUP = `
  <svg viewBox="0 0 20 20" width="22" height="22" aria-hidden="true"
       style="display:block;filter:drop-shadow(0 1px 2px rgba(0,0,0,0.45));">
    <path d="M10 1.8 L18.6 17 L1.4 17 Z"
          fill="#facc15" stroke="#111827" stroke-width="1.4" stroke-linejoin="round"/>
    <rect x="9" y="7" width="2" height="5.4" rx="0.4" fill="#111827"/>
    <circle cx="10" cy="14.4" r="1.15" fill="#111827"/>
  </svg>
`;
